import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'

import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'

import type { BudgetFilter, BudgetFilterValue, FilterCategory } from '../FiltersPanelV2/types'

const useCanteenMenuFiltersStateUserDataFragment = graphql(`
  fragment UseCanteenMenuFiltersState_UserDataFragment on User {
    allergens {
      celery
      cereals
      crustaceans
      eggs
      fish
      lupin
      milk
      molluscs
      mustard
      nuts
      peanuts
      sesame
      soybeans
      sulphur
    }
    dietaryPreferences {
      vegan
      vegetarian
      halal
      kosher
      noRefinedSugar: refinedSugar
    }
    account {
      topUpEnabled
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

type Props = {
  userData?: FragmentType<typeof useCanteenMenuFiltersStateUserDataFragment> | null
  paramsVendors: string
  paramsDietaries: string
  paramsAllergens: string
}

export const useCcMenuFiltersState = ({
  userData: userDataProp,
  paramsAllergens,
  paramsDietaries,
  paramsVendors,
}: Props) => {
  const userData = useFragment(useCanteenMenuFiltersStateUserDataFragment, userDataProp)
  const useCanteenMenuCreditFilter = useFeatureFlag(SPLITS.USE_CANTEEN_MENU_CREDIT_FILTER)
  const [dietaries, setDietaries] = useState<string[]>([])
  const [allergens, setAllergens] = useState<string[]>([])
  const [vendors, setVendors] = useState<string[]>([])
  const [budgetFilter, setBudgetFilter] = useState<BudgetFilter>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')

  const setMultipleValues = useCallback((category: FilterCategory, values: string[]) => {
    match(category)
      .with('dietaries', () => {
        setDietaries((prev) => Array.from(new Set([...prev, ...values])))
      })
      .with('allergens', () => {
        setAllergens((prev) => Array.from(new Set([...prev, ...values])))
      })
      .with('vendors', () => {
        setVendors((prev) => Array.from(new Set([...prev, ...values])))
      })
      .with('teams', () => {})
      .exhaustive()
  }, [])

  // handle any incoming filters from the URL
  useEffect(() => {
    const allergensFromParams = paramsAllergens.length > 0 ? paramsAllergens.split(',') : []
    const dietariesFromParams = paramsDietaries.length > 0 ? paramsDietaries.split(',') : []
    const vendorsFromParams = paramsVendors.length > 0 ? paramsVendors.split(',') : []
    setMultipleValues('allergens', allergensFromParams)
    setMultipleValues('vendors', vendorsFromParams)
    setMultipleValues('dietaries', dietariesFromParams)
  }, [paramsAllergens, paramsDietaries, paramsVendors, setMultipleValues])

  // handle user-specific filters from their profile settings
  useEffect(() => {
    if (!userData) return
    const userAllergens = { ...userData.allergens }
    delete userAllergens.__typename
    const userDietaryPreferences = { ...userData.dietaryPreferences }
    delete userDietaryPreferences.__typename

    const allergensToSet = Object.entries(userAllergens)
      .filter(([, value]) => value)
      .map(([key]) => key)

    const dietariesToSet = Object.entries(userDietaryPreferences)
      .filter(([, value]) => value)
      .map(([key]) => key)

    if (allergensToSet.includes('nuts')) {
      allergensToSet.push('peanuts')
    }

    if (allergensToSet.length) setMultipleValues('allergens', allergensToSet)
    if (dietariesToSet.length) setMultipleValues('dietaries', dietariesToSet)
  }, [userData, setMultipleValues])

  // default to 'within subsidy' for accounts that can't top up, since they can't
  // spend above their subsidy/credits balance. Only applied once so it doesn't
  // override a user's manual toggle if userData is refetched. Skipped when the
  // Budget filter UI isn't shown (flag off / prices hidden), since there'd be
  // no way for the user to see or clear it.
  const hasAppliedDefaultBudgetFilter = useRef(false)
  const hideCCPrices = Boolean(userData?.account?.userInterfaceSettings?.hideCCPrices)
  useEffect(() => {
    if (hasAppliedDefaultBudgetFilter.current) return
    if (!userData) return
    if (userData.account?.topUpEnabled === false && useCanteenMenuCreditFilter && !hideCCPrices) {
      setBudgetFilter('subsidy')
    }
    hasAppliedDefaultBudgetFilter.current = true
  }, [userData, hideCCPrices, useCanteenMenuCreditFilter])

  const handleToggle = (value: string, setValue: Dispatch<SetStateAction<string[]>>) => {
    setValue((prev) => {
      const newValue = [...prev]
      const indexOfValue = newValue.indexOf(value)
      if (indexOfValue === -1) newValue.push(value)
      else newValue.splice(indexOfValue, 1)
      return newValue
    })
  }

  const toggleFilterValue = (category: FilterCategory, value: string) => {
    match(category)
      .with('allergens', () => handleToggle(value, setAllergens))
      .with('dietaries', () => handleToggle(value, setDietaries))
      .with('vendors', () => handleToggle(value, setVendors))
      .with('teams', () => {})
      .exhaustive()
  }

  const toggleBudgetFilter = (value: BudgetFilterValue) => {
    setBudgetFilter((prev) => (prev === value ? null : value))
  }

  const clearFilters = () => {
    setDietaries([])
    setAllergens([])
    setVendors([])
    setBudgetFilter(null)
  }

  return {
    filters: {
      dietaries,
      allergens,
      vendors,
    },
    searchTerm,
    budgetFilter,
    toggleBudgetFilter,
    setSearchTerm,
    toggleFilterValue,
    clearFilters,
  }
}
